import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import PhaseList from "../PhaseList";
import QuestionItemWrapper from "../QuestionItemWrapper";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { getFormData } from "../../../Redux/actions/responseAction";
import { SxProps } from "@mui/material";
import { Stack } from "@mui/system";
import {
  setPhaseList,
  setResponseIsEditable,
  setResponseLoader,
  setScreeningAttemptId,
  setScreeningBtn,
  setSelectedPhase,
} from "../../../Redux/reducers/responseSlice";
import { VisitPhaseRoot } from "../../../types/VisitPhases.types";
import { PhasesList } from "../types";
import { handlePhaseStatus } from "../utils";
import { buildPhaseListForm } from "../formCompletion";
import FinishReviewModal from "./FinishReviewModal";

const rootResponses: SxProps = {
  width: "100%",
  height: "calc(100vh - 194px)",
  display: "flex",
  p: 1,
  overflow: "auto",
  position: "relative",
  alignItems: "flex-start",
};

const ScreeningResponses = ({
  showModal,
  setShowModal,
  changeStatus = false,
  setChangeStatus,
}: any) => {
  const { id: studyId, participantId } = useParams();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const togglePhase = useAppSelector((state) => state.response.togglePhase);
  const selectedPhase = useAppSelector((state) => state.response.selectedPhase);
  const canEditScreening = useAppSelector(
    (state) => state.response.canEditScreening
  );

  const fetchStudyPhases = useCallback(async () => {
    try {
      if (studyId && participantId) {
        setIsLoading(true);
        dispatch(setResponseLoader(true));
        let params: any = {
          participantId,
        };

        const attemptsRes = await http.get(
          `/study/${studyId}/admin-screening-responses/attempts`,
          { params }
        );
        const attempts = attemptsRes.data?.data || [];
        const participantAttempt = attempts.find(
          (attempt: any) => attempt.actorId === null
        );
        const adminAttempt = attempts.find(
          (attempt: any) => attempt.actorId !== null
        );
        const attemptId = adminAttempt
          ? adminAttempt.id
          : participantAttempt?.id;

        if (!attemptId) {
          dispatch(setResponseIsEditable(canEditScreening));
          dispatch(setScreeningBtn("create"));
          dispatch(setResponseLoader(false));
          setIsLoading(false);
          return;
        }

        dispatch(
          setResponseIsEditable(
            canEditScreening
              ? adminAttempt
                ? !adminAttempt.isReadOnly
                : false
              : false
          )
        );
        dispatch(
          setScreeningBtn(
            adminAttempt
              ? participantAttempt
                ? "compare"
                : "complete"
              : "create"
          )
        );
        dispatch(
          setScreeningAttemptId({
            screeningAttemptId: attemptId,
          })
        );

        const { data } = await http.get<VisitPhaseRoot>(
          `/study/${studyId}/study-phase/participant/screening`,
          { params }
        );

        if (data?.data.length === 0) {
          // Set Reducer Form Loading State to false
          dispatch(setResponseLoader(false));
          setIsLoading(false);
          return;
        }

        const phases: PhasesList[] = data?.data
          .map((phase) => {
            const phaseForms = phase.phaseForms
              .map((phaseForm) => {
                const formAttempt = phaseForm.form.formAttempts
                  ? phaseForm.form.formAttempts.find(
                      (attempt: any) =>
                        attempt?.screeningAttempt?.id === attemptId
                    )
                  : null;
                return buildPhaseListForm(phaseForm, phase, formAttempt);
              })
              .sort((a, b) => a.position - b.position);

            const phaseStatus = handlePhaseStatus(phaseForms);

            return {
              id: phase.id,
              name: phase.name,
              position: phase.position,
              phaseStatus: phaseStatus,
              phaseForms: phaseForms,
            };
          })
          .sort((a, b) => a.position - b.position);

        let selectedForm = null;
        if (phases.length > 0) {
          const formId = sessionStorage.getItem("response-visit-form");
          let selPhase = phases?.[0]?.id;

          if (formId) {
            phases.forEach((phase) => {
              const selForm = phase.phaseForms.find((pf) => pf.id === formId);
              if (selForm) {
                selPhase = phase.id;
                selectedForm = selForm;
              }
            });
          }
          if (!selectedForm) {
            // When locked: prefer form with attempt. When editable: prefer incomplete.
            const allForms = phases.flatMap((p) =>
              (p.phaseForms || []).map((pf) => ({ phaseId: p.id, form: pf }))
            );
            const screeningEditable = adminAttempt
              ? !adminAttempt.isReadOnly
              : false;
            let pick = null;
            if (!screeningEditable) {
              pick =
                allForms.find((item) => !!item.form.attempt) || allForms[0];
            } else {
              const incomplete = allForms.find(
                (item) => item.form.status !== "completed"
              );
              const inProgress = allForms.find(
                (item) =>
                  item.form.status === "active" ||
                  item.form.status === "inprogress"
              );
              pick = incomplete || inProgress || allForms[0];
            }
            if (pick) {
              selectedForm = pick.form;
              selPhase = pick.phaseId;
            }
          }
          if (selectedForm) {
            dispatch(getFormData({ studyId, selectedForm, participantId }));
          } else {
            dispatch(setResponseLoader(false));
          }
          dispatch(setSelectedPhase({ phase: selPhase }));
        }

        dispatch(setPhaseList({ list: phases }));
        setIsLoading(false);
      }
    } catch (error) {
      dispatch(setResponseLoader(false));
      setIsLoading(false);
      errorToastMessage(error as Error);
    }
  }, [studyId, participantId, dispatch, togglePhase, canEditScreening]);

  useEffect(() => {
    fetchStudyPhases();
  }, [fetchStudyPhases]);

  const closeModal = () => {
    setShowModal(false);
    setChangeStatus?.(false);
  };

  return (
    <Stack direction={"row"} sx={rootResponses}>
      {/* Phases and Forms */}
      <PhaseList
        isLoading={isLoading}
        selectedPhaseId={selectedPhase}
        currentTab={"Screening"}
        showCollapse={true}
      />

      {/* Question List */}
      <QuestionItemWrapper />
      {showModal && (
        <FinishReviewModal
          showModal={showModal}
          closeModal={closeModal}
          changeStatus={changeStatus}
        />
      )}
    </Stack>
  );
};
export default ScreeningResponses;
